import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:updated', this.#updateProgressBar);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:updated', this.#updateProgressBar);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    this.#updateProgressBar();
  };

  #updateProgressBar = async () => {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    // cart.total_price vem em centavos
    const currentTotalCents = cart.total_price;

    const checkpointsJson = this.querySelector('#progress-checkpoints');
    if (!checkpointsJson) {
      return;
    }
    const checkpointsText = checkpointsJson.textContent;
    if (!checkpointsText) {
      return;
    }
    let checkpoints;
    try {
      checkpoints = JSON.parse(checkpointsText);
    } catch (error) {
      console.error('Error parsing checkpoints JSON:', error);
      return;
    }

    if (!Array.isArray(checkpoints) || checkpoints.length === 0) return;

    // Valores vêm em reais do JSON, converter para centavos para comparação
    // Se o valor for menor que 1, assumir que foi salvo incorretamente em centavos e já está em reais
    checkpoints.forEach(cp => {
      // Se valor < 1, pode ser que foi salvo como centavos (ex: 0.20 = 20 centavos)
      // Mas como queremos trabalhar sempre em reais, vamos assumir que valores < 1 são erros
      // e precisam ser multiplicados por 100 para converter centavos para reais primeiro
      let valueInReais = cp.value;
      if (valueInReais > 0 && valueInReais < 1) {
        // Valor parece estar em centavos (ex: 0.20), converter para reais (20)
        valueInReais = valueInReais * 100;
      }
      cp.valueCents = valueInReais * 100;
    });
    checkpoints.sort((a, b) => a.valueCents - b.valueCents);
    const maxValueCents = checkpoints[checkpoints.length - 1].valueCents;
    
    let progressPercentage;
    if (maxValueCents === 0) {
      progressPercentage = 100;
    } else {
      progressPercentage = Math.min((currentTotalCents / maxValueCents) * 100, 100);
    }
    
    // Garantir que nunca ultrapasse 100%
    progressPercentage = Math.min(progressPercentage, 100);

    /** @type {{value: number, valueCents: number, id: number} | null} */
    let nextCp = null;
    for (const cp of checkpoints) {
      if (cp.valueCents > currentTotalCents) {
        if (nextCp === null || cp.valueCents < nextCp.valueCents) {
          nextCp = { value: cp.value, valueCents: cp.valueCents, id: cp.id };
        }
      }
    }

    const progressFill = this.querySelector('.progress-fill-enhanced');
    if (progressFill instanceof HTMLElement) {
      // Garantir que o width nunca ultrapasse 100%
      const finalWidth = Math.min(progressPercentage, 100);
      progressFill.style.width = `${finalWidth}%`;
    }

    const messageElem = this.querySelector('.progress-message-enhanced');
    if (messageElem instanceof HTMLElement) {
      if (nextCp && nextCp !== null) {
        // Calcular diferença em centavos e converter para reais
        const remainingCents = nextCp.valueCents - currentTotalCents;
        const remainingReais = remainingCents / 100;
        const formattedRemaining = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(remainingReais);
        const messageElement = this.querySelector(`#checkpoint-messages [data-id="${nextCp.id}"]`);
        const messageContent = messageElement ? messageElement.textContent || messageElement.innerHTML : '';
        const progressText = messageElem.querySelector('.progress-text');
        if (progressText) {
          progressText.innerHTML = messageContent.replace('{amount}', formattedRemaining);
          progressText.classList.remove('progress-complete');
        }
      } else {
        const completionElement = this.querySelector('#completion-message');
        const progressText = messageElem.querySelector('.progress-text');
        if (progressText) {
          progressText.innerHTML = completionElement ? completionElement.textContent || completionElement.innerHTML : '';
          progressText.classList.add('progress-complete');
        }
      }
    }

    this.querySelectorAll('.checkpoint-enhanced').forEach(el => {
      if (!(el instanceof HTMLElement)) return;
      const datasetValue = el.dataset.value;
      if (!datasetValue) return;
      // data-value já está em centavos (vem do Liquid)
      const cpValueCents = parseFloat(datasetValue);
      if (isNaN(cpValueCents)) return;
      if (cpValueCents <= currentTotalCents) {
        el.classList.add('reached');
      } else {
        el.classList.remove('reached');
      }
    });
  };

  open() {
    this.showDialog();
    this.#updateProgressBar();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
